// Daily localStorage cleanup utility - triggers on first visit each day
export class DailyStorageCleanup {
  private static readonly LAST_CLEANUP_KEY = 'lastStorageCleanupDate';
  
  /**
   * Performs daily cleanup of localStorage if needed
   * Call this from App.tsx or main entry points - triggers on first visit each day
   */
  static performDailyCleanup(): boolean {
    if (!this.shouldRunCleanup()) {
      return false;
    }

    
    try {
      const results = {
        favoritedLineups: this.cleanupFavoritedLineups(),
        lateSwapLineups: this.cleanupLateSwapLineups(),
        // oldLineupBuilderData: this.cleanupOldLineupBuilderData(),
        // expiredControls: this.cleanupExpiredGameControls(),
        // generalCleanup: this.cleanupExpiredItems()
      };

      // Mark cleanup as completed for today
      this.markCleanupCompleted();
      
      const totalCleaned = Object.values(results).reduce((sum, count) => sum + count, 0);
      console.log('cleaned up', totalCleaned);
      return true;
    } catch (error) {
      console.error('❌ Error during daily cleanup:', error);
      return false;
    }
  }

  /**
   * Check if cleanup should run - first visit of the day
   */
  private static shouldRunCleanup(): boolean {
    const lastCleanup = localStorage.getItem(this.LAST_CLEANUP_KEY);
    
    if (!lastCleanup) {
      return true; // Never cleaned before
    }

    const lastCleanupDate = new Date(lastCleanup);
    const today = new Date();
    
    // Check if it's a different day (not just 24 hours)
    const todayDateString = today.toDateString();
    const lastCleanupDateString = lastCleanupDate.toDateString();

    // Run cleanup if it's a new day
    return todayDateString !== lastCleanupDateString;
  }

  /**
   * Alternative: Force cleanup regardless of timing (for manual triggers)
   */
  static forceCleanup(): boolean {
    try {
      const results = {
        favoritedLineups: this.cleanupFavoritedLineups(),
        lateSwapLineups: this.cleanupLateSwapLineups(),
        // oldLineupBuilderData: this.cleanupOldLineupBuilderData(),
        // expiredControls: this.cleanupExpiredGameControls(),
        // generalCleanup: this.cleanupExpiredItems()
      };

      this.markCleanupCompleted();
      
      const totalCleaned = Object.values(results).reduce((sum, count) => sum + count, 0);
      
      return true;
    } catch (error) {
      console.error('❌ Error during forced cleanup:', error);
      return false;
    }
  }

  /**
   * Clean up favorited lineups older than specified days
   */
  private static cleanupFavoritedLineups(maxAgeDays: number = 3): number {
    const keysToRemove: string[] = [];

    // Find all favorited lineup keys
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Match favorited lineup patterns
      if (key.startsWith('precontest-lineups-') || key.startsWith('lateswap-lineups') || key.startsWith('lineupbuilder-favorites')) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            // Since we can't easily determine slate age, remove all old favorited lineups daily
            // This ensures fresh start each day while preserving current day's work
            keysToRemove.push(key);
          }
        } catch (e) {
          // Remove corrupted entries
          keysToRemove.push(key);
        }
      }

      // Also clean up precontest boosts, exposures, and player groups
      if (key.startsWith('preContest-roiBoost-') ||
          key.startsWith('preContest-stackBoost-') ||
          key.startsWith('preContest-roiExposure-') ||
          key.startsWith('preContest-stackExposure-') ||
          key.startsWith('precontest-playerGroups-')) {
        keysToRemove.push(key);
      }
    }

    // Remove identified keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    return keysToRemove.length;
  }

  /**
   * Clean up late swap lineups (can be more aggressive since they're time-sensitive)
   */
  private static cleanupLateSwapLineups(): number {
    const keysToRemove: string[] = [];

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key === 'lateswap-lineups') {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    return keysToRemove.length;
  }

  /**
   * Clean up old LineupBuilder data (beyond expiry system)
   */
  private static cleanupOldLineupBuilderData(): number {
    const keysToRemove: string[] = [];

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;

      // LineupBuilder patterns that might be stuck
      if (key.startsWith('lineupBuilder-') || key.startsWith('lineupBuilderName-')) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            
            // If it has timestamp structure, let the expiry system handle it
            if (parsed.timestamp) {
              const now = Date.now();
              const expirationMs = (parsed.expirationHours || 336) * 60 * 60 * 1000;
              if ((now - parsed.timestamp) > expirationMs) {
                keysToRemove.push(key);
              }
            } else {
              // Old format without expiry - remove if older than 14 days
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // Remove corrupted entries
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    return keysToRemove.length;
  }

  /**
   * Clean up corrupted game controls and boost settings
   */
  private static cleanupExpiredGameControls(): number {
    const keysToRemove: string[] = [];

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Game control patterns - only remove corrupted ones, keep user preferences
      if (key.includes('GameControls') || 
          key.includes('boost') || 
          key.includes('removedTeams') ||
          key.startsWith('easyProjectionGameControls-')) {
        
        try {
          const data = localStorage.getItem(key);
          if (data) {
            JSON.parse(data); // Test if parseable
            // Keep valid game controls as they're user preferences
          }
        } catch (e) {
          // Remove corrupted entries
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    return keysToRemove.length;
  }

  /**
   * Use existing expiry system to clean up expired items
   */
  private static cleanupExpiredItems(): number {
    try {
      // Import the utility dynamically to avoid circular dependencies
      const { localStorageWithExpiry } = require('./localStorageWithExpiry');
      return localStorageWithExpiry.clearExpired() || 0;
    } catch (e) {
      console.warn('Could not access localStorageWithExpiry utility');
      return 0;
    }
  }

  /**
   * Mark cleanup as completed for today
   */
  private static markCleanupCompleted(): void {
    const now = new Date();
    localStorage.setItem(this.LAST_CLEANUP_KEY, now.toISOString());
  }

  /**
   * Get information about when cleanup last ran
   */
  static getCleanupInfo(): { 
    lastCleanup: Date | null; 
    willRunToday: boolean;
    daysSinceLastCleanup: number;
  } {
    const lastCleanupStr = localStorage.getItem(this.LAST_CLEANUP_KEY);
    const lastCleanup = lastCleanupStr ? new Date(lastCleanupStr) : null;
    
    const willRunToday = this.shouldRunCleanup();
    
    let daysSinceLastCleanup = 0;
    if (lastCleanup) {
      const now = new Date();
      const timeDiff = now.getTime() - lastCleanup.getTime();
      daysSinceLastCleanup = Math.floor(timeDiff / (1000 * 3600 * 24));
    }

    return { lastCleanup, willRunToday, daysSinceLastCleanup };
  }

  /**
   * Get current localStorage usage statistics
   */
  static getStorageStats(): { 
    totalItems: number; 
    estimatedSizeKB: number; 
    favoritedLineupsCount: number;
    lineupBuilderCount: number;
  } {
    let totalItems = 0;
    let estimatedSize = 0;
    let favoritedLineupsCount = 0;
    let lineupBuilderCount = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      totalItems++;
      const value = localStorage.getItem(key) || '';
      estimatedSize += key.length + value.length;

      if (key.startsWith('precontest-lineups-') || key.startsWith('lateswap-lineups')) {
        favoritedLineupsCount++;
      } else if (key.startsWith('lineupBuilder')) {
        lineupBuilderCount++;
      }
    }

    return {
      totalItems,
      estimatedSizeKB: Math.round(estimatedSize / 1024),
      favoritedLineupsCount,
      lineupBuilderCount
    };
  }
}